import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { AlertService } from '../alert/alert.service';
import { ILogger, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { Channels } from 'src/app/store/channels';
import { GeneralSignalRResponse, RestrictionImpactSignalRResponse } from './general-signal-r-response';

export class MyLogger implements ILogger {

  public logLevel!: BehaviorSubject<number | null>;

  constructor() {
    this.logLevel = new BehaviorSubject<number | null>(null);
  }
  log(logLevel: LogLevel) {
    if (logLevel > 0) {
      this.setLogLevel(logLevel);
    }
  }

  private setLogLevel(value: number) {
    this.logLevel.next(value);
  }

  getLogLevel(): Observable<number | null> {
    return this.logLevel.asObservable();
  }

}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  BASE_URL = environment.urls.baseApiUrl.replace('api', 'apphub');
  private readonly HUB_TOKEN_URL = environment.urls.baseApiUrl + '/Authentication/HubToken';
  public hubConnection?: signalR.HubConnection;
  firstLoad = true;
  connection = false;
  reconnected = false;
  /** Emits each time the hub connection is re-established, so open forms can re-sync data they may have missed. */
  public reconnected$ = new Subject<void>();
  private reconnectPending = false;
  private stopped = false;

  logger = new MyLogger();

  logLevelSubscription?: Subscription;

  public senderSubject: BehaviorSubject<number | null>;
  private restrictionImpactSubject = new Subject<RestrictionImpactSignalRResponse>();
  public restrictionImpact$ = this.restrictionImpactSubject.asObservable();

  constructor(
    private alert: AlertService,
    private http: HttpClient
  ) {
    this.checkLogger();
    this.senderSubject = new BehaviorSubject<number | null>(null);
  }

  private checkLogger() {
    this.logger.logLevel?.subscribe(() => {
      const state = this.hubConnection?.state;
      if (state == 'Disconnected') {
        this.connection = false;
      }
      if (state == 'Connected') {
        this.connection = true;
        if (!this.firstLoad)
          this.reconnected = true;
      }
    });
  }

  /**
   * Builds the hub connection, registers application-level event handlers and connects when a session token exists.
   * The hub requires authentication (WI 6947): a short-lived hub-only token travels through accessTokenFactory
   * and the connection only starts once the user is logged in; see fetchHubToken(), connect() and stopConnection().
   */
  public startConnection() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.BASE_URL, { accessTokenFactory: () => this.fetchHubToken() })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 15000])
      .configureLogging(this.logger)
      .build();

    this.hubConnection.keepAliveIntervalInMilliseconds = 5000;
    this.hubConnection.serverTimeoutInMilliseconds = 60000;
    this.hubConnection.on(Channels.RESTRICTION_IMPACT, (data: GeneralSignalRResponse) => {
      const impact = data?.obj as RestrictionImpactSignalRResponse;
      if (impact && (impact.refreshAll || Array.isArray(impact.acceleratorIDs))) {
        this.restrictionImpactSubject.next(impact);
      }
    });

    this.connect();

    this.hubConnection.onreconnecting(() => {
      this.connection = false;
      this.alert.error("Lost Connection to Server. Attempting to Reconnect...");
    });

    this.hubConnection.onreconnected(connectionId => {
      this.connection = true;
      this.alert.success('Reconnected successfully!');
      console.log('Reconnected successfully with connection ID:', connectionId);
      this.reconnected$.next();
    });

    this.hubConnection.onclose(() => {
      this.connection = false;
      if (this.stopped) return;
      this.reconnectPending = true;
      console.log("Connection closed. Trying to reconnect...");
      this.alert.error('Connection closed. Trying to reconnect...');
      setTimeout(() => { this.startConnection(); }, 2000);  // Reconnect logic
    });
  }

  /**
   * Exchanges the session JWT (sent by the token interceptor) for a hub-only token holding just the user id.
   * WebSockets put the token in the query string and the full session JWT exceeds the IIS 2048-character
   * limit for users with many roles (WI 6947). SignalR calls this on negotiate and on every reconnect,
   * so the token's few-minute lifetime is never a problem. Without a session there is nothing to exchange.
   */
  private fetchHubToken(): Promise<string> {
    if (!localStorage.getItem('Token')) return Promise.resolve('');
    return firstValueFrom(this.http.get<{ token: string }>(this.HUB_TOKEN_URL).pipe(map(response => response.token)));
  }

  /** Starts the hub connection when a session token exists and the connection is not already active. */
  public connect() {
    if (!localStorage.getItem('Token') || !this.hubConnection) return;
    if (this.hubConnection.state !== signalR.HubConnectionState.Disconnected) return;
    this.stopped = false;
    this.startHubConnection();
  }

  /** Stops the hub connection on logout and suppresses the automatic restart in onclose. */
  public stopConnection() {
    this.stopped = true;
    this.connection = false;
    this.hubConnection?.stop().catch(err => console.log('Error while stopping connection: ' + err));
  }

  public listenOn(tag: string) {
    this.hubConnection?.on(tag, (data) => {
      console.log(data);
    });
  }

  startHubConnection() {
    this.hubConnection?.start()
      .then(() => {
        this.connection = true;
        if (this.reconnectPending) {
          // A closed connection was rebuilt from scratch; pushes sent meanwhile were lost.
          this.reconnectPending = false;
          this.reconnected$.next();
        }
        if (!this.firstLoad) {
          this.firstLoad = false;
          console.log('Connection started');
          this.reconnected = true;
          this.alert.info('Connection Restablished!');
        }
      })
      .catch(err => {
        console.log('Error while starting connection: ' + err);
        if (!this.stopped) setTimeout(() => this.connect(), 3000);  // Reconnect logic
      }
      );
  }

  public listenOn2 = (tag: string, funct: (...args: any[]) => any) => this.hubConnection?.on(tag, funct);

  setSender(value: number | null) {
    this.senderSubject.next(value);
  }
}
